import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { isoDate, formatCurrency, MONTH_NAMES } from "@/lib/format";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function History() {
  const { t } = useLang();
  const nav = useNavigate();
  const [cursor, setCursor] = useState(new Date());
  const [entries, setEntries] = useState({});
  const [holidays, setHolidays] = useState(new Set());

  useEffect(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const start = isoDate(new Date(y, m, 1));
    const end = isoDate(new Date(y, m + 1, 0));
    api.get("/entries", { params: { start, end } }).then(({ data }) => {
      const map = {};
      (data.entries || []).forEach((e) => { map[e.date] = { ...e.result.summary, day_type: e.day_type || "mdm" }; });
      setEntries(map);
    });
    api.get("/holidays", { params: { start, end } }).then(({ data }) => setHolidays(new Set(data.dates || []))).catch(() => {});
  }, [cursor]);

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const first = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="space-y-5">
      <h1 className="text-2xl md:text-3xl font-heading font-extrabold">{t("history")}</h1>
      <div className="card-thali">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCursor(new Date(y, m - 1, 1))} className="w-11 h-11 rounded-lg bg-thali-cream grid place-items-center" data-testid="hist-prev"><ChevronLeft /></button>
          <div className="font-heading font-bold text-lg">{MONTH_NAMES[m + 1]} {y}</div>
          <button onClick={() => setCursor(new Date(y, m + 1, 1))} className="w-11 h-11 rounded-lg bg-thali-cream grid place-items-center" data-testid="hist-next"><ChevronRight /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs text-thali-ink/50 mb-1">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} />;
            const ds = isoDate(new Date(y, m, d));
            const has = entries[ds];
            const dt = has ? has.day_type : null;
            const hol = holidays.has(ds);
            const dotColor = dt === "dry_ration" ? "bg-thali-turmericdark" : dt === "holiday" ? "bg-gray-400" : "bg-thali-green";
            const bg = dt === "holiday" ? "bg-gray-100" : dt === "dry_ration" ? "bg-thali-turmeric/15" : has ? "bg-thali-green/10 font-bold" : hol ? "bg-thali-saffron/10" : "hover:bg-thali-cream";
            return (
              <button key={i} onClick={() => nav(`/app/entry?date=${ds}`)} data-testid={`hist-day-${d}`}
                className={`aspect-square rounded-lg flex flex-col items-center justify-center relative text-sm transition-colors ${bg}`}>
                {d}
                {has && <span className={`w-1.5 h-1.5 rounded-full ${dotColor} absolute bottom-1`} data-testid={`hist-dot-${d}`} />}
                {hol && !has && <span className="text-[8px] text-thali-saffron font-bold absolute bottom-0.5">H</span>}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-3 mt-3 text-xs text-thali-ink/70" data-testid="hist-legend">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-thali-green" /> 🍛 MDM Served</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-thali-turmericdark" /> 🌾 Dry Ration</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-400" /> 🏖️ Holiday</span>
        </div>
      </div>

      <div className="space-y-2">
        {Object.entries(entries).sort().map(([d, s]) => (
          <button key={d} onClick={() => nav(`/app/entry?date=${d}`)} className="w-full card-thali p-3 flex items-center justify-between" data-testid={`hist-item-${d}`}>
            <span className="font-semibold">{d.split("-").reverse().join("-")}</span>
            <span className="text-sm text-thali-ink/70">{s.total_students} students · <b className="text-thali-green">{formatCurrency(s.grand_total)}</b></span>
          </button>
        ))}
        {Object.keys(entries).length === 0 && <div className="text-center py-6 text-thali-ink/50">No entries this month.</div>}
      </div>
    </div>
  );
}
